/**
 * CatalogMultiPickerModal — Phase 2A (2026-09-02).
 *
 * Modal de sélection multiple depuis le catalogue de postes pour insertion
 * batch dans une section de devis.
 *
 * Contrat :
 *   - Ouvre depuis le menu "Ajouter une ligne" > "Depuis le catalogue".
 *   - Recherche débounced (300 ms) sur label + pro_description + section.
 *   - Filtre par section (Select shadcn, "Toutes les sections" par défaut).
 *   - Table checkboxes multi-select. Colonnes : label, section, unit, unit_price, vat_rate.
 *   - Footer : bouton "Insérer N lignes" (N = count sélection, disabled si 0).
 *   - Design système Wérko : borders slate-700, mode clair/sombre auto.
 *
 * TVA : la ligne insérée hérite du fix hotfix `addFromCatalog` (priorité
 * `item.default_vat_rate`, sinon `companySettings.default_vat_rate`). Ce
 * composant n'introduit AUCUNE valeur littérale de TVA.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { BookOpen, Search } from "lucide-react";
import { formatCurrencyBE } from "@/lib/currency";

export default function CatalogMultiPickerModal({
  open,
  onClose,
  items,
  onConfirm, // (selectedItems: item[]) => void
  defaultSection,
}) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [sectionFilter, setSectionFilter] = useState("__ALL__");

  // Reset state à chaque ouverture
  useEffect(() => {
    if (open) {
      setQuery("");
      setDebouncedQuery("");
      setSelectedIds(new Set());
      // Phase 2A fix UX : ne présélectionne la section que si elle EXISTE
      // dans le catalogue (sinon la table serait vide au chargement).
      const hasSection =
        defaultSection &&
        (items || []).some((it) => (it.section || "") === defaultSection);
      setSectionFilter(hasSection ? defaultSection : "__ALL__");
    }
  }, [open, defaultSection, items]);

  // Debounce recherche 300 ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Sections uniques disponibles
  const sections = useMemo(() => {
    const set = new Set();
    (items || []).forEach((it) => {
      if (it.section) set.add(it.section);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "fr"));
  }, [items]);

  // Filtrage
  const filtered = useMemo(() => {
    const q = debouncedQuery;
    return (items || []).filter((it) => {
      if (sectionFilter !== "__ALL__" && (it.section || "") !== sectionFilter) {
        return false;
      }
      if (!q) return true;
      return (
        (it.label || "").toLowerCase().includes(q) ||
        (it.pro_description || "").toLowerCase().includes(q) ||
        (it.section || "").toLowerCase().includes(q)
      );
    });
  }, [items, debouncedQuery, sectionFilter]);

  const toggleId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === filtered.length) return new Set();
      return new Set(filtered.map((it) => it.id));
    });
  };

  const handleConfirm = () => {
    const selected = (items || []).filter((it) => selectedIds.has(it.id));
    if (selected.length === 0) return;
    onConfirm(selected);
    onClose();
  };

  const selectedCount = selectedIds.size;
  const allVisible = filtered.length > 0 && selectedCount === filtered.length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-h-[90vh] max-w-3xl overflow-hidden border-slate-800 bg-slate-950 p-0 sm:rounded-lg"
        data-testid="catalog-multi-picker-modal"
      >
        <DialogHeader className="border-b border-slate-800 px-4 py-3 sm:px-6">
          <DialogTitle className="flex items-center gap-2 text-base text-slate-100 sm:text-lg">
            <BookOpen className="h-5 w-5 text-[#E9A98F]" />
            Sélection depuis le catalogue
          </DialogTitle>
        </DialogHeader>

        {/* Filtres */}
        <div className="border-b border-slate-800 bg-slate-900/40 px-4 py-3 sm:px-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher un poste (libellé, description, section)…"
                className="border-slate-700 bg-slate-950 pl-9 text-sm text-slate-100"
                data-testid="catalog-multi-picker-search"
                autoFocus
              />
            </div>
            <select
              value={sectionFilter}
              onChange={(e) => setSectionFilter(e.target.value)}
              className="rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-[#C4553B] sm:min-w-[200px]"
              data-testid="catalog-multi-picker-section-filter"
            >
              <option value="__ALL__">Toutes les sections</option>
              {sections.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table sélection */}
        <div
          className="max-h-[50vh] overflow-y-auto"
          data-testid="catalog-multi-picker-table"
        >
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              Aucun poste ne correspond aux critères.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 border-b border-slate-800 bg-slate-900 text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="py-2 pl-4 pr-2 text-left">
                    <Checkbox
                      checked={allVisible}
                      onCheckedChange={toggleAll}
                      data-testid="catalog-multi-picker-toggle-all"
                    />
                  </th>
                  <th className="py-2 pr-3 text-left font-medium">Libellé</th>
                  <th className="hidden py-2 pr-3 text-left font-medium sm:table-cell">
                    Section
                  </th>
                  <th className="py-2 pr-3 text-left font-medium">Unité</th>
                  <th className="py-2 pr-3 text-right font-medium">Prix</th>
                  <th className="py-2 pr-4 text-right font-medium">TVA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {filtered.map((it) => {
                  const checked = selectedIds.has(it.id);
                  return (
                    <tr
                      key={it.id}
                      onClick={() => toggleId(it.id)}
                      className={`cursor-pointer hover:bg-slate-800/40 ${
                        checked ? "bg-[#C4553B]/10" : ""
                      }`}
                      data-testid={`catalog-multi-picker-row-${it.id}`}
                    >
                      <td className="py-2 pl-4 pr-2">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleId(it.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <div className="font-medium text-slate-100">{it.label}</div>
                        {it.pro_description && (
                          <div className="mt-0.5 text-xs text-slate-500 line-clamp-1">
                            {it.pro_description}
                          </div>
                        )}
                      </td>
                      <td className="hidden py-2 pr-3 text-slate-400 sm:table-cell">
                        {it.section || "—"}
                      </td>
                      <td className="py-2 pr-3 text-slate-300">{it.unit || "—"}</td>
                      <td className="py-2 pr-3 text-right text-slate-300">
                        {it.unit_price != null
                          ? formatCurrencyBE(it.unit_price)
                          : "—"}
                      </td>
                      <td className="py-2 pr-4 text-right text-slate-300">
                        {it.default_vat_rate != null ? `${it.default_vat_rate} %` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="border-t border-slate-800 bg-slate-900/40 px-4 py-3 sm:px-6">
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-between">
            <Button
              variant="outline"
              onClick={onClose}
              className="border-slate-700 bg-slate-800/60 text-slate-200"
              data-testid="catalog-multi-picker-cancel"
            >
              Annuler
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={selectedCount === 0}
              className="bg-[#C4553B] text-white hover:bg-[#B04930] disabled:opacity-40"
              data-testid="catalog-multi-picker-confirm"
            >
              Insérer {selectedCount} ligne{selectedCount > 1 ? "s" : ""}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
